/**
 * An image of the career site: a link (http or https), an image uploaded to BestConsultant, kept as
 * `asset:<id>` (a CareerAsset of the API, served to everyone by `GET /api/jobBoards/careerAssets/:id`),
 * or, for the backgrounds, a built-in pattern kept as `pattern:<id>` and drawn in the theme's colours.
 * The text of an offer embeds an uploaded image by that public URL.
 */

export const CAREER_ASSET_PREFIX = 'asset:'
const ASSET_REF = /^asset:([0-9a-f]{24})$/
const ASSET_REF_GLOBAL = /asset:([0-9a-f]{24})/g
const ASSET_URL_GLOBAL = /\/careerAssets\/([0-9a-f]{24})/g
const MAX_IMAGE_URL_LENGTH = 2048

/** Id of the uploaded image a value points to, null for a link or anything else */
export const careerAssetId = (value: unknown) =>
  typeof value === 'string' ? ASSET_REF.exec(value.trim())?.[1] ?? null : null

export const careerAssetRef = (id: string) => `${CAREER_ASSET_PREFIX}${id}`

/** Built-in background patterns: fine lines and dots drawn in CSS by the career pages, some fading out */
export const CAREER_BACKGROUND_PATTERNS = ['grid', 'gridFade', 'graph', 'dots', 'dotsFade', 'crosshatch', 'crosshatchFade', 'plusGrid', 'diagonal', 'lines', 'checker'] as const
export type TCareerBackgroundPattern = (typeof CAREER_BACKGROUND_PATTERNS)[number]
export const CAREER_PATTERN_PREFIX = 'pattern:'

/** Pattern a value points to, null for an image or anything else */
export const careerPatternId = (value: unknown): TCareerBackgroundPattern | null => {
  if (typeof value !== 'string' || !value.startsWith(CAREER_PATTERN_PREFIX))
    return null
  const id = value.slice(CAREER_PATTERN_PREFIX.length)
  return CAREER_BACKGROUND_PATTERNS.includes(id as TCareerBackgroundPattern) ? id as TCareerBackgroundPattern : null
}

export const careerPatternRef = (id: TCareerBackgroundPattern) => `${CAREER_PATTERN_PREFIX}${id}`

/**
 * An uploaded image (`asset:<id>`) or an http(s) URL, normalized by `URL` so that it can sit inside a
 * CSS `url("...")` (quotes are percent-encoded). Anything else becomes `null`.
 */
export const sanitizeCareerImage = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim() || value.length > MAX_IMAGE_URL_LENGTH)
    return null
  const assetId = careerAssetId(value)
  if (assetId)
    return careerAssetRef(assetId)
  const patternId = careerPatternId(value.trim())
  if (patternId)
    return careerPatternRef(patternId)
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  }
  catch {
    return null
  }
}

/** Every uploaded image a value (theme, sections, offer and its text...) points to, at any depth */
export const collectCareerAssetIds = (value: unknown): string[] => {
  const ids = new Set<string>()
  const visit = (item: unknown) => {
    if (typeof item === 'string') {
      for (const match of item.matchAll(ASSET_REF_GLOBAL))
        ids.add(match[1])
      for (const match of item.matchAll(ASSET_URL_GLOBAL))
        ids.add(match[1])
    }
    else if (Array.isArray(item)) {
      item.forEach(visit)
    }
    else if (typeof item === 'object' && item !== null) {
      Object.values(item).forEach(visit)
    }
  }
  visit(value)
  return [...ids]
}
